import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { AIInteractionAudit, User, COLLECTION_NAMES } from '../types';

/**
 * Interface for audit statistics
 */
export interface AuditStats {
  totalInteractions: number;
  totalCost: number;
  evaluationCount: number;
  chatCount: number;
  avgResponseTime: number;
  securityIncidents: number;
}

/**
 * Interface for daily statistics
 */
export interface DailyStats {
  date: string;
  interactions: number;
  cost: number;
}

/**
 * Interface for top user statistics
 */
export interface TopUser {
  user_id: string;
  user_name: string;
  interactions: number;
  total_cost: number;
  avg_response_time: number;
}

/**
 * Interface for security incidents
 */
export interface SecurityIncident {
  id: string;
  user_name: string;
  interaction_type: string;
  contained_pii: boolean;
  pii_types: string[];
  injection_attempt: boolean;
  injection_patterns: string[];
  created_at: string;
}

/**
 * Hook for managing AI Usage statistics with Firestore
 * Replaces Supabase queries with Firestore v9+ modular API
 */
export function useAIUsage() {
  const [loading, setLoading] = useState(true);
  const [todayStats, setTodayStats] = useState<AuditStats | null>(null);
  const [monthStats, setMonthStats] = useState<AuditStats | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [securityIncidents, setSecurityIncidents] = useState<SecurityIncident[]>([]);
  const { eosUsers = [] } = useAuth();

  /**
   * Calculate statistics from audit records
   */
  const calculateStats = useCallback((records: AIInteractionAudit[]): AuditStats => {
    const totalInteractions = records.length;
    const totalCost = records.reduce((sum, r) => sum + (r.estimatedCostUsd || 0), 0);
    const evaluationCount = records.filter(r => r.interactionType === 'evaluation').length;
    const chatCount = records.filter(r => r.interactionType === 'chat_message').length;
    const avgResponseTime = records.length > 0
      ? records.reduce((sum, r) => sum + (r.responseTimeMs || 0), 0) / records.length
      : 0;
    const securityIncidents = records.filter(r => r.containedPii || r.injectionAttempt).length;

    return {
      totalInteractions,
      totalCost,
      evaluationCount,
      chatCount,
      avgResponseTime,
      securityIncidents,
    };
  }, []);

  /**
   * Load today's statistics
   */
  const loadTodayStats = useCallback(async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = Timestamp.fromDate(today);

      const q = query(
        collection(db, COLLECTION_NAMES.AI_INTERACTION_AUDIT),
        where('createdAt', '>=', todayTimestamp),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const records = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIInteractionAudit[];

      const stats = calculateStats(records);
      setTodayStats(stats);
    } catch (error) {
      console.error('Error loading today stats:', error);
    }
  }, [calculateStats]);

  /**
   * Load month's statistics
   */
  const loadMonthStats = useCallback(async () => {
    try {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const startTimestamp = Timestamp.fromDate(startOfMonth);

      const q = query(
        collection(db, COLLECTION_NAMES.AI_INTERACTION_AUDIT),
        where('createdAt', '>=', startTimestamp),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const records = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIInteractionAudit[];

      const stats = calculateStats(records);
      setMonthStats(stats);
    } catch (error) {
      console.error('Error loading month stats:', error);
    }
  }, [calculateStats]);

  /**
   * Load daily statistics for the last 30 days
   */
  const loadDailyStats = useCallback(async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0);
      const startTimestamp = Timestamp.fromDate(thirtyDaysAgo);

      const q = query(
        collection(db, COLLECTION_NAMES.AI_INTERACTION_AUDIT),
        where('createdAt', '>=', startTimestamp),
        orderBy('createdAt', 'asc')
      );

      const snapshot = await getDocs(q);
      const records = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIInteractionAudit[];

      const dailyMap = new Map<string, { interactions: number; cost: number }>();

      records.forEach((record) => {
        const date = record.createdAt instanceof Timestamp
          ? record.createdAt.toDate().toISOString().split('T')[0]
          : new Date(record.createdAt).toISOString().split('T')[0];
        const existing = dailyMap.get(date) || { interactions: 0, cost: 0 };
        dailyMap.set(date, {
          interactions: existing.interactions + 1,
          cost: existing.cost + (record.estimatedCostUsd || 0),
        });
      });

      const dailyArray = Array.from(dailyMap.entries()).map(([date, stats]) => ({
        date,
        interactions: stats.interactions,
        cost: stats.cost,
      }));

      setDailyStats(dailyArray);
    } catch (error) {
      console.error('Error loading daily stats:', error);
    }
  }, []);

  /**
   * Load top users for the current month
   */
  const loadTopUsers = useCallback(async () => {
    try {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const startTimestamp = Timestamp.fromDate(startOfMonth);

      const q = query(
        collection(db, COLLECTION_NAMES.AI_INTERACTION_AUDIT),
        where('createdAt', '>=', startTimestamp),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const records = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIInteractionAudit[];

      const userMap = new Map<string, { name: string; interactions: number; cost: number; totalTime: number }>();

      records.forEach((record) => {
        const userId = record.userId;
        const user = eosUsers.find(u => u.id === userId);
        const userName = user?.name || 'Unknown User';
        const existing = userMap.get(userId) || { name: userName, interactions: 0, cost: 0, totalTime: 0 };

        userMap.set(userId, {
          name: userName,
          interactions: existing.interactions + 1,
          cost: existing.cost + (record.estimatedCostUsd || 0),
          totalTime: existing.totalTime + (record.responseTimeMs || 0),
        });
      });

      const topUsersArray: TopUser[] = Array.from(userMap.entries())
        .map(([userId, stats]) => ({
          user_id: userId,
          user_name: stats.name,
          interactions: stats.interactions,
          total_cost: stats.cost,
          avg_response_time: stats.interactions > 0 ? stats.totalTime / stats.interactions : 0,
        }))
        .sort((a, b) => b.total_cost - a.total_cost)
        .slice(0, 10);

      setTopUsers(topUsersArray);
    } catch (error) {
      console.error('Error loading top users:', error);
    }
  }, [eosUsers]);

  /**
   * Load security incidents
   */
  const loadSecurityIncidents = useCallback(async () => {
    try {
      // Firestore doesn't support OR queries directly, so we need to fetch both and combine
      const piiQuery = query(
        collection(db, COLLECTION_NAMES.AI_INTERACTION_AUDIT),
        where('containedPii', '==', true),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const injectionQuery = query(
        collection(db, COLLECTION_NAMES.AI_INTERACTION_AUDIT),
        where('injectionAttempt', '==', true),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const [piiSnapshot, injectionSnapshot] = await Promise.all([
        getDocs(piiQuery),
        getDocs(injectionQuery),
      ]);

      const piiRecords = piiSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIInteractionAudit[];

      const injectionRecords = injectionSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIInteractionAudit[];

      // Combine and deduplicate by ID
      const allRecordsMap = new Map<string, AIInteractionAudit>();
      [...piiRecords, ...injectionRecords].forEach(record => {
        allRecordsMap.set(record.id, record);
      });

      const incidents: SecurityIncident[] = Array.from(allRecordsMap.values())
        .map((record) => {
          const user = eosUsers.find(u => u.id === record.userId);
          return {
            id: record.id,
            user_name: user?.name || 'Unknown User',
            interaction_type: record.interactionType,
            contained_pii: record.containedPii || false,
            pii_types: record.piiTypes || [],
            injection_attempt: record.injectionAttempt || false,
            injection_patterns: record.injectionPatterns || [],
            created_at: record.createdAt instanceof Timestamp
              ? record.createdAt.toDate().toISOString()
              : new Date(record.createdAt).toISOString(),
          };
        })
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 50);

      setSecurityIncidents(incidents);
    } catch (error) {
      console.error('Error loading security incidents:', error);
    }
  }, [eosUsers]);

  /**
   * Load all statistics
   */
  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadTodayStats(),
        loadMonthStats(),
        loadDailyStats(),
        loadTopUsers(),
        loadSecurityIncidents(),
      ]);
    } catch (error) {
      console.error('Error loading AI usage stats:', error);
    } finally {
      setLoading(false);
    }
  }, [loadTodayStats, loadMonthStats, loadDailyStats, loadTopUsers, loadSecurityIncidents]);

  /**
   * Export data to CSV
   */
  const exportToCSV = useCallback(async () => {
    try {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const startTimestamp = Timestamp.fromDate(startOfMonth);

      const q = query(
        collection(db, COLLECTION_NAMES.AI_INTERACTION_AUDIT),
        where('createdAt', '>=', startTimestamp),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const records = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIInteractionAudit[];

      const csv = [
        ['Date', 'User', 'Type', 'Model', 'Prompt Tokens', 'Response Tokens', 'Cost', 'Response Time (ms)', 'PII Detected', 'Injection Attempt'].join(','),
        ...records.map((record) => {
          const user = eosUsers.find(u => u.id === record.userId);
          const date = record.createdAt instanceof Timestamp
            ? record.createdAt.toDate().toISOString()
            : new Date(record.createdAt).toISOString();
          return [
            date,
            user?.name || 'Unknown',
            record.interactionType,
            record.modelUsed || 'N/A',
            record.promptTokens || 0,
            record.responseTokens || 0,
            (record.estimatedCostUsd || 0).toFixed(4),
            record.responseTimeMs || 0,
            record.containedPii ? 'Yes' : 'No',
            record.injectionAttempt ? 'Yes' : 'No',
          ].join(',');
        }),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-usage-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      throw error;
    }
  }, [eosUsers]);

  // Load stats when users are loaded
  useEffect(() => {
    if (eosUsers.length > 0) {
      loadStats();
    }
  }, [eosUsers, loadStats]);

  return {
    loading,
    todayStats,
    monthStats,
    dailyStats,
    topUsers,
    securityIncidents,
    exportToCSV,
    refreshStats: loadStats,
  };
}

